import { useRef, useSyncExternalStore } from 'react'

/**
 * Minimal external store for client state (module-level stores are shared by the whole client app).
 * The server snapshot is always `initialState`.
 */
const createStore = <T>(initialState: T) => {
    const listeners = new Set<(state: T) => void>()

    let currentState = initialState

    const dispatch = () => listeners.forEach(listener => listener(currentState))
    const subscribe = (listener: (state: T) => void) => {
        listeners.add(listener)
        return () => listeners.delete(listener)
    }
    const getServerSnapshot = () => initialState

    return {
        get: () => currentState,
        set: (newState: T) => {
            currentState = newState
            dispatch()
        },
        subscribe,
        useStore: () => useSyncExternalStore(subscribe, () => currentState, getServerSnapshot),
        useStoreWithSelector: <K extends keyof T>(selector: K): T[K] => {
            return useSyncExternalStore(
                subscribe,
                () => currentState[selector],
                () => initialState[selector]
            )
        },
        /**
         * Subscribes to a derived value. The result is memoized per (state, selector), so a selector returning
         * a fresh object/array (e.g. `s => s.items.filter(...)` or `s => s[id] ?? {}`) does not loop; the
         * component still re-renders whenever the store changes unless `isEqual` says the result is the same.
         */
        useStoreWithSelectorFunction: <R>(
            selector: (store: T) => R,
            isEqual: (a: R, b: R) => boolean = Object.is
        ): R => {
            const cache = useRef<{ state: T; selector: (store: T) => R; result: R } | null>(null)

            const select = (state: T) => {
                const cached = cache.current
                if (cached && cached.state === state && cached.selector === selector)
                    return cached.result

                const result = selector(state)
                if (cached && isEqual(cached.result, result)) {
                    cache.current = { state, selector, result: cached.result }
                    return cached.result
                }

                cache.current = { state, selector, result }
                return result
            }

            return useSyncExternalStore(
                subscribe,
                () => select(currentState),
                () => select(initialState)
            )
        },
    }
}

export default createStore
